import { defineComponent as _defineComponent } from 'vue'
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { computed, ref } from 'vue';
import GridBuyBox from '@/features/buybox/components/grid/GridBuyBox.vue';
import { MonetizationType, PresentationType, TitleListType } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { isTitleInList } from '@/helpers/title-helper';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { getExcludedPackageShortNames } from '@/constants/package-exclusion';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxBanner',
  props: {
    offerCount: null,
    offers: null,
    title: null,
    seasonNumber: { default: null },
    additionalContexts: null,
    isAVODActive: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { offerCount: number, offers: TitleOfferFragment[], title: TitleDetail, seasonNumber: number | null, additionalContexts: SnowplowContext[], isAVODActive?: boolean };

const { t, tc } = useI18n();
const { country, language } = storeToRefs(useLanguageStore());
const { isLoggedIn, isProUser } = storeToRefs(useUserStore());

const showBuyBox = ref(false);



const showDetails = computed(() => {
	if (props.title.__typename !== 'Season') return null;
	return {
		id: props.title.id,
		title: props.title.content.title,
		objectId: props.title.show.objectId,
		objectType: props.title.show.objectType,
	};
});

function toggleBuyBox() {
	showBuyBox.value = !showBuyBox.value;

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'dropdown_clicked', label: 'buybox', value: showBuyBox.value ? 1 : 0 },
		props.additionalContexts
	);
}

function scrollTo(id: string) {
	const element = document.getElementById(id);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
	TrackingHelper.trackEvent('userinteraction', {
		action: 'jumplink_clicked',
		label: id.replaceAll('-', '_'),
	});
}

const showTheatreAndProviderText = computed(() => {
	return ['en', 'fr', 'de', 'es', 'tr', 'pt', 'it'].includes(language.value);
});

const excludedPackages = computed(() => {
	const entityId = props.title.__typename === 'Season' ? props.title.show.id : props.title.id;

	return getExcludedPackageShortNames(isLoggedIn.value, entityId, country.value, isProUser.value);
});

const hasOnlyCinemaOffers = computed(() => {
	return props.offers.every(offer => offer.monetizationType === MonetizationType.Cinema);
});

const cinemaOffersCount = computed(() => {
	return props.offers.filter(offer => offer.monetizationType === MonetizationType.Cinema).length;
});

const dvdBluRayOffersCount = computed(() => {
	return props.offers.filter(offer =>
		[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(offer.presentationType)
	).length;
});

const providerCount = computed(() => {
	// START AVOD
	// Only show One Plex offer at a time.
	// + Always filtering out Justwatch-plex Provider as it's only used by partners.
	const AVODTechnicalName = props.isAVODActive
		? ['plex', 'justwatchplexchannel']
		: ['plexplayer', 'justwatchplexchannel'];

	return [...new Map(props.offers.map(offer => [offer.package.packageId, offer])).values()].filter(
		offer =>
			// Filtering out TVOD providers we don't have a partnership with
			!excludedPackages.value.includes(offer.package.shortName) &&
			// Only show one plex offer
			!AVODTechnicalName.includes(offer.package.technicalName) &&
			// Filter out DVD/BluRay offers
			![PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
				offer.presentationType
			)
	).length;
});

const allOffersCount = computed(() => providerCount.value + dvdBluRayOffersCount.value);

const nonCinemaOffersCount = computed(() => {
	return providerCount.value - cinemaOffersCount.value;
});

const offerCountText = computed(() => {
	if (hasOnlyCinemaOffers.value) return t('WEBAPP_CURRENTLY_IN_THEATERS');

	if (cinemaOffersCount.value > 0 && showTheatreAndProviderText.value) {
		if (nonCinemaOffersCount.value === 1) {
			if (dvdBluRayOffersCount.value > 0) {
				return tc('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE_DISC');
			}
			return tc('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE');
		}

		if (cinemaOffersCount.value > 0 && dvdBluRayOffersCount.value > 0 && nonCinemaOffersCount.value === 0) {
			return tc('WEBAPP_CURRENTLY_IN_THEATERS_DISC');
		}

		if (dvdBluRayOffersCount.value > 0) {
			return t('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES_DISC', {
				count: nonCinemaOffersCount.value,
			});
		}

		return t('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES', {
			count: nonCinemaOffersCount.value,
		});
	}

	if (providerCount.value === 1) {
		if (dvdBluRayOffersCount.value > 0) {
			return tc('WEBAPP_AVAIL_ON_STREAMING_SERVICE_DISC');
		}
		return tc('WEBAPP_AVAIL_ON_STREAMING_SERVICE');
	}

	if (dvdBluRayOffersCount.value > 0 && providerCount.value > 1) {
		return t('WEBAPP_AVAIL_ON_STREAMING_SERVICES_DISC', {
			count: providerCount.value,
		});
	}

	if (dvdBluRayOffersCount.value > 0 && providerCount.value === 0) {
		return t('WEBAPP_AVAIL_ON_DISC', {
			count: dvdBluRayOffersCount.value,
		});
	}

	return t('WEBAPP_AVAIL_ON_STREAMING_SERVICES', {
		count: providerCount.value,
	});
});

return { __sfc: true,t, tc, country, language, isLoggedIn, isProUser, showBuyBox, props, showDetails, toggleBuyBox, scrollTo, showTheatreAndProviderText, excludedPackages, hasOnlyCinemaOffers, cinemaOffersCount, dvdBluRayOffersCount, providerCount, allOffersCount, nonCinemaOffersCount, offerCountText, GridBuyBox, TitleListType, isTitleInList, faChevronUp, faChevronDown }
}

})